import { cn } from '@/lib/utils';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import {
  UNDERWRITING_STATUS_CONFIG,
  FundingPolicyType,
  isUnderwritingEnabledForPolicyType,
} from '@shared';
import { Button, Spinner } from '@design-system';
import { ChevronRight, RotateCcw, Sparkles } from 'lucide-react';
import { UnderwritingSlideOut } from './underwriting-slide-out';
import { QuestionnaireDialog } from '@/features/client-quick-plan/components/questionnaire-dialog';
import { useUnderwritingStatus } from '../hooks/use-underwriting-status';

interface UnderwritingStatusBadgeProps {
  productName: string;
  fundingPolicyType: FundingPolicyType;
  policyType?: string;
  className?: string;
  size?: 'sm' | 'md'; // sm for compact badges (like in FundingTable), md for quote cards (default)
  /**
   * `quote-card-link` is the quote card's check as a text link, not a third button beside "Select
   * Product" and "Create Quote", because it is a check rather than a decision (UX walkthrough,
   * 19 Oct 2026). It fills the width it is given and centres on it: the cards give it the buttons'
   * width, so it lines up under them and wraps inside it. Its result is a link too, labelled so it
   * reads as the check's outcome: "Eligibility check: Likely Eligible ›" (review, 20 Oct 2026).
   */
  appearance?: 'badge' | 'quote-card-link';
}

// In the card link the icon sits in the line of text, so a label too long for the buttons' width
// wraps as one centred, balanced block with the icon leading it.
const CARD_LINK_ICON_CLASSES = 'mr-1.5 inline align-[-0.125em]';

export function UnderwritingStatus({
  productName,
  fundingPolicyType,
  policyType,
  className,
  size = 'md',
  appearance = 'badge',
}: UnderwritingStatusBadgeProps) {
  const { flags } = useFeatureFlags();
  const underwritingEnabled = isUnderwritingEnabledForPolicyType(fundingPolicyType, flags);

  const {
    status,
    underwritingResult,
    structuredResult,
    pipelineMetadata,
    hasUnderwritingResult,
    isSampleClient,
    isLoading,
    hasCheckFailed,
    needsMedicalQuestionnaire,
    showSlideOut,
    setShowSlideOut,
    showQuestionnaireDialog,
    setShowQuestionnaireDialog,
    questionnaireVariant,
    questionnaireClient,
    handleCheckEligibilityClick,
    handleViewDetails,
  } = useUnderwritingStatus({
    productName,
    fundingPolicyType,
    policyType,
  });

  if (flags.nycLifeDemo) return null;

  const isCardLink = appearance === 'quote-card-link';

  // A product the analysis does not cover yet says so up front, as a label and not a button.
  // It used to show the normal button, which only answered with a passing "coming soon" message
  // once clicked (UX walkthrough, 9 Oct 2026).
  if (!underwritingEnabled) {
    return (
      <span
        data-testid="underwriting-coming-soon"
        className={cn(
          'text-muted-foreground items-center gap-1.5 font-medium',
          isCardLink
            ? 'block w-full text-center text-sm text-balance'
            : 'border-border bg-muted inline-flex h-7 w-fit rounded-full border px-2 text-xs',
          className,
        )}
      >
        <Sparkles className={cn('size-3 shrink-0', isCardLink && CARD_LINK_ICON_CLASSES)} />
        Eligibility Check Coming Soon
      </span>
    );
  }

  const displayedStatus = status;

  // Show CTA button when status doesn't exist
  if (!displayedStatus) {
    // A sample result is loaded from a server-side fixture. Never expose a trigger while that
    // initial request is in flight (or if it fails), because sample underwriting is read-only.
    if (isSampleClient) return null;

    // A check that ended with nothing to show says so, instead of looking as if it was never run.
    const showsFailure = hasCheckFailed && !isLoading;

    return (
      <>
        <Button
          variant={isCardLink ? 'link' : 'ghost'}
          size="sm"
          onClick={handleCheckEligibilityClick}
          disabled={isLoading}
          className={cn(
            isCardLink
              ? 'text-primary hover:text-primary-dark block h-auto w-full whitespace-normal text-balance !px-0 py-1 text-center text-sm font-medium'
              : 'border-primary-300 bg-primary-50 text-primary-dark hover:bg-primary-100 !h-7 w-fit self-start !rounded-full border !px-2 text-xs font-medium',
            showsFailure &&
              (isCardLink
                ? 'text-destructive hover:text-destructive'
                : 'border-destructive/30 bg-destructive/5 text-destructive hover:border-destructive/40 hover:bg-destructive/10'),
            className,
          )}
          data-testid="get-underwriting-eligibility-cta"
        >
          {isLoading ? (
            <>
              <Spinner className={cn('h-3 w-3', isCardLink && CARD_LINK_ICON_CLASSES)} />
              Checking...
            </>
          ) : showsFailure ? (
            <>
              <RotateCcw className={cn('!size-3', isCardLink && CARD_LINK_ICON_CLASSES)} />
              {isCardLink ? (
                // Too long for the buttons' width: it breaks between the two sentences.
                <>
                  <span className="whitespace-nowrap">Couldn&apos;t complete.</span>{' '}
                  <span className="whitespace-nowrap">Try again</span>
                </>
              ) : (
                <>Couldn&apos;t complete. Try again</>
              )}
            </>
          ) : (
            <>
              <Sparkles className={cn('!size-3', isCardLink && CARD_LINK_ICON_CLASSES)} />
              {/* Without the medical questionnaire a click opens the dialog that sends or fills
                  it, so the label says that up front, in the banner's own words. */}
              {needsMedicalQuestionnaire ? 'Unlock Eligibility Check' : 'Run Eligibility Check'}
              {!isCardLink && <ChevronRight className="!size-3" />}
            </>
          )}
        </Button>

        {/* Questionnaire Dialog */}
        <QuestionnaireDialog
          open={showQuestionnaireDialog}
          onOpenChange={setShowQuestionnaireDialog}
          variant={questionnaireVariant}
          client={questionnaireClient}
        />
      </>
    );
  }

  const config = UNDERWRITING_STATUS_CONFIG[displayedStatus];
  if (!config) return null;

  // A column, so the hidden trigger the slide-out renders beside the result takes no height.
  return (
    <div className={cn(isCardLink ? 'flex flex-col' : 'contents')}>
      {isCardLink ? (
        <Button
          variant="link"
          size="sm"
          onClick={handleViewDetails}
          disabled={!hasUnderwritingResult}
          data-testid="quote-detail-eligibility-status"
          className={cn(
            'group block h-auto w-full whitespace-normal text-balance !px-0 py-1 text-center text-sm font-medium hover:no-underline',
            className,
          )}
          aria-label={`View ${config.label} details`}
        >
          {/* Too long for the buttons' width: it breaks between the label and the outcome. */}
          <span className="text-muted-foreground whitespace-nowrap">Eligibility check:</span>{' '}
          <span
            className={cn(
              'whitespace-nowrap underline-offset-4 group-hover:underline',
              config.classes.textColor,
            )}
          >
            {config.label}
            <ChevronRight className="ml-0.5 inline size-3.5 align-[-0.15em]" />
          </span>
        </Button>
      ) : (
        <button
          type="button"
          onClick={handleViewDetails}
          disabled={!hasUnderwritingResult}
          data-testid="quote-detail-eligibility-status"
          className={cn(
            'inline-flex items-center gap-1.5 border font-medium transition-all',
            'hover:shadow-sm disabled:pointer-events-none disabled:opacity-50',
            hasUnderwritingResult && 'cursor-pointer',
            'h-7 rounded-full px-2 text-xs',
            size === 'sm' ? 'w-max' : 'w-fit',
            config.classes.bgColor,
            config.classes.textColor,
            config.classes.borderColor,
            config.classes.hoverBgColor,
            className,
          )}
          aria-label={`View ${config.label} details`}
        >
          {config.label}
          <ChevronRight className="h-3 w-3" />
        </button>
      )}

      {/* Slideout for viewing underwriting details */}
      <UnderwritingSlideOut
        open={showSlideOut}
        onClose={() => setShowSlideOut(false)}
        underwritingResult={underwritingResult}
        structuredResult={structuredResult}
        pipelineMetadata={pipelineMetadata}
        productName={productName}
        fundingPolicyType={fundingPolicyType}
      />
    </div>
  );
}
